import { useMemo, useRef } from 'react';
import { useRelayEnvironment, type Environment } from 'react-relay';
import {
	getForgeUIExtensionsRelayAsync,
	type GetForgeUIExtensionsAsyncOptions,
} from '../../../provider/getForgeUIExtensionsAsync';
import {
	GQLExtensionContextsFilterType,
	type GQLExtensionRenderingContextInput,
} from '../../../web-client';
import { type TargetURLUtils } from './navigation-targets/common';
import isEqual from 'lodash/isEqual';
import type { ForgeUIExtensionType } from '@atlassian/forge-ui-types';
import type { GraphQLError } from 'graphql';

const useCustomCompareMemo = <T, D>(
	factory: () => T,
	dependency: D,
	isEqual: (a: D, b: D) => boolean,
): T => {
	const dependencyRef = useRef<D>(dependency);
	const valueRef = useRef<T>(factory());

	if (!isEqual(dependency, dependencyRef.current)) {
		dependencyRef.current = dependency;
		valueRef.current = factory();
	}

	// eslint-disable-next-line react-hooks/exhaustive-deps
	return useMemo(() => valueRef.current as T, [dependencyRef.current]);
};

type UseGetAppForgeUIExtensionsParams = {
	appId?: string;
	bypassNewForgeExtensionListCall?: boolean;
	cloudId?: string;
	context?: GQLExtensionRenderingContextInput;
	contextIds: string[];
	environmentId?: string;
	includeHidden?: boolean;
	moduleType?: string;
	workspaceARI?: string;
};

type GetAppForgeUIExtensionDeps = [Environment, string | undefined, string | undefined, string[]];

const isSameDeps = (a: GetAppForgeUIExtensionDeps, b: GetAppForgeUIExtensionDeps) => {
	if (a === b) {
		return true;
	}
	if (!a || !b) {
		return false;
	}

	const [relayEnvironmentA, appIdA, environmentIdA, contextIdsA] = a;
	const [relayEnvironmentB, appIdB, environmentIdB, contextIdsB] = b;

	return (
		relayEnvironmentA === relayEnvironmentB &&
		appIdA === appIdB &&
		environmentIdA === environmentIdB &&
		isEqual(contextIdsA.sort(), contextIdsB.sort())
	);
};

export const useGetAppForgeUIExtensions = ({
	appId,
	environmentId,
	contextIds,
	cloudId,
	includeHidden,
	moduleType,
	bypassNewForgeExtensionListCall,
}: UseGetAppForgeUIExtensionsParams): ((
	param: Omit<
		Parameters<
			({
				contextIds,
				moduleType,
				context,
				queryOptions,
				expandAppOwner,
				extensionsFilter,
				dataClassificationTagIds,
				locale,
				cloudId,
				includeHidden,
				bypassNewForgeExtensionListCall,
			}: GetForgeUIExtensionsAsyncOptions) => Promise<{
				errors?: ReadonlyArray<GraphQLError | Error>;
				extensions: ReadonlyArray<ForgeUIExtensionType> | null;
			}>
		>[0],
		'client' | 'contextIds' | 'moduleType' | 'extensionsFilter'
	>,
) => ReturnType<
	({
		contextIds,
		moduleType,
		context,
		queryOptions,
		expandAppOwner,
		extensionsFilter,
		dataClassificationTagIds,
		locale,
		cloudId,
		includeHidden,
		bypassNewForgeExtensionListCall,
	}: GetForgeUIExtensionsAsyncOptions) => Promise<{
		errors?: ReadonlyArray<GraphQLError | Error>;
		extensions: ReadonlyArray<ForgeUIExtensionType> | null;
	}>
>) => {
	const relayEnvironment = useRelayEnvironment();
	return useCustomCompareMemo<
		TargetURLUtils['getAppForgeUIExtensions'],
		GetAppForgeUIExtensionDeps
	>(
		() => {
			return async (params) => {
				return await getForgeUIExtensionsRelayAsync({
					contextIds,
					extensionsFilter: [
						{
							type: GQLExtensionContextsFilterType.AppIdEnvironmentId,
							value: [`${appId}:${environmentId}`],
						},
					],
					...params,
					cloudId,
					includeHidden,
					moduleType,
					bypassNewForgeExtensionListCall,
					relayEnvironment,
				});
			};
		},
		[relayEnvironment, appId, environmentId, contextIds],
		isSameDeps,
	);
};
